import React from "react"
import { connect } from "react-redux"
import { Col } from "react-bootstrap"

import type { Map, List } from "immutable"

// components
import ResourceTable from "app/static/frontend/sheets/components/ResourceTable"

// action creators
import { initSheet, storeResourceTableConfiguration } from "app/static/frontend/sheets/sheet-action-creators"

import sheetConstants from "app/static/frontend/sheets/constants"

import { areSheetColumnsEqual } from "app/static/frontend/sheets/helper-functions"

export interface ColumnPropType {
    /**
     * if the field requires a ColumnSearchGridToggle, optionally specify the string for the filter's true label
     * otherwise the default value is "Yes"
     */
    columnSearchTrueLabel?: string

    /**
     * if the field requires a ColumnSearchGridToggle, optionally specify the string for the filter's false label
     * otherwise the default value is "No"
     */
    columnSearchFalseLabel?: string

    /**
     * if you have custom display logic, pass in a function of the type
     * (...args, { columnIndex, optionalActionCreator, rowIndex, tableId}) => <Component />
     * where the return type is either a valid React component or a basic type, such as Number, String, etc.
     */
    display?: (...args: any[]) => React.ComponentType<any>

    /**
     * if provided filter the enums that will be shown in the filter dropdown if the column
     * has filtering enabled
     */
    enumFilter?: (...args: any[]) => any

    /**
     * an optional key to access the enum, if the label is not desired. Used both in the cell values
     * as well as the enum options that show up in the filter select dropdown
     */
    enumLabel?: string

    /** the field on the API resource you would like to pull from to populate the column */
    field: string | string[]

    /** if the field is a foreign key, need to specify what field on the foreign key to grab */
    foreignKeyField?: string

    /** provide the ability to invert the default sorting order */
    invert_sorting?: boolean

    /** the name of the column to show on the frontend */
    name: string

    /**
     * Provide a custom Cell to render, instead of DefaultCell. Must adhere to the same
     * rules and structure of Sheet cells. Must be a function - otherwise will default to DefaultCell
     */
    optionalResourceTableCell: (...args: any[]) => any

    /**
     * allow for providing a filter key, which will be used when make API filtering requests by the table
     * this is particularly useful when a field can't be found through the DjangIO serialization system
     */
    overrideFilterKey?: string

    /** enable the ability to sort the column - field must have no dehydrate method on the resource */
    resourceTableEnableSorting?: boolean

    /** enable the ability to filter the column - field must have no dehydrate method on the resource */
    resourceTableEnableFiltering?: boolean

    /**
     * enable the ability to customize which field the column should be filtered on. Defaults to
     * field if not provided. Filtering is disable if provided multiple fields and resourceTableFilterOn is not provided
     */
    resourceTableFilterOn?: string

    /**
     * enable the ability to customize which field the column should be sorted on. Defaults to
     * field if not provided. Sorting is disable if provided multiple fields and resourceTableSortOn is not provided
     */
    resourceTableSortOn?: string

    /**
     * if the sort we would like to perform is performed via a querymethod of the relevant Queryset class
     * this is the name of the querymethod
     * NOTE: this will override resourceTableSortOn
     */
    columnSortingQuerymethod?: string

    /** give a width for the column */
    width?: number

    /**
     * design system resource tables allows the entire row to be clickable (not just the DatumCell) and access
     * selectCellLink. Enabling this prop will disable the cellLink for the individual column.
     */
    resourceTableDisableCellLink?: boolean
}

export interface ResourceTableContainerProps {
    columns: ColumnPropType[] | List<ColumnPropType> | List<Map<string, any>>

    /**
     * Can the user remove rows from the table by hovering over an IndexCell (the cells that are numbered)
     * defaults to false
     */
    canRemoveRows?: boolean
    name?: string
    dataType: number
    /** Do not link any cells in this row to their specified cellLink */
    disableRowLink?: boolean
    helpCenterUrl?: string
    /**
     * key is referenced here as a workaround for a timing issue with how ResourceTableContainer
     * accesses the Redux state -- PropTypes should not normally refer to key, which is for internal
     * React use only and is not a regular prop.
     *
     * Details: The timing issue in the coupling between ResourceTableContainer and the Redux store
     * requires id and key to change in sync, so that whenever id is changed, the component is remounted
     * with a new key. A fix might be to check, when rendering, whether the Redux store has been updated
     * to include the table's new state.
     * TODO(QPAC-844): Fix the rendering problem and remove this from propTypes.
     *
     * Update: Unfortunately, this PropType cannot be enforced -- it will always just generate a console error,
     * since the PropTypes library is no longer even allowed to inspect key -- in current versions of React.
     * key: PropTypes.string.isRequired,
     *
     * The id for the table. This will be used to keep track of the table in the Redux store. Normally, this
     * should be static, as re-rendering will get the table out of sync with the Redux store temporarily.
     * However, if multiple tables will be visible simultaneously, such as in a dashboard, and their settings
     * are to be independent, then they need distinct id's.
     * If id needs to vary, set the "key" prop to change along with it, so that the Redux store and the
     * mounted component stay in sync.
     */
    id: string
    /** whether this ResourceTable is for recovering deleted items */
    isUndelete?: boolean
    /**
     * specify an optional action creator that is called when clicking on the numbered
     * IndexCells (only relevant for tables with canRemoveRows=true)
     */
    removeRow?: any
    /** the tooltip to render when hover over the datum column text */
    resourceTableTooltipText?: string
    /** the tooltip to render in the datum cells */
    resourceTableDatumTooltipText?: string
    /** a DjangIO resource, representing one of our Django models */
    rowResource: any
    /** optional filter for the endpoint */
    rowFilter: any
    /** the number of rows to load when paginating the table */
    scrollInterval?: number
    /** should the table display a full page loading spinner on initial load */
    shouldShowLoadingSpinnerInitially?: boolean
    /**
     * should the table display the NoResults component even if there are no filters applied
     * this is only relevant if there is not an associated Feature enum item for the QDT of the table rows
     * Empty state logic:
     * if the QDT of the rows is part of the Feature enum (defined in app/models), then an empty state can be defined for the resource table
     * this empty state is shown if there are no results and no filters (logic in selectShouldShowEmptyState)
     * if the QDT is not associated with a Feature, then a NoResults component is shown based on logic in selectNoResults
     * this prop makes selectNoResults return true if there is no row data
     * if this prop is false then selectNoResults will only return true if there is no row data but there are filters (selectNoResultsAndNoFilters is false)
     */
    shouldShowNoResultsWithoutFilters?: boolean
    /*
     * Should the Loading spinner be shown. This is by default false, but allows the developer
     * to show Loading state after the table initially loads
     */
    resourceTableShowLoadingSpinner?: boolean
    /*
     * override the sweet alert that is shown when a row is deleted from the table, if the table
     * supports removing rows
     */
    swalConfig?: any
    /**
     * The datum cells are configured to build a url from the dataType
     * This allows you to override the url, that is of the form quorum.us/[urlPath]/:id
     * leaving this empty will result in default settings
     * You must provide a url of the form '/someUrl' or '/account/manage_org'
     */
    datumUrlOverride?: string
    initSheet?: (...args: any[]) => any
    /**
     * pass a function (with its variables already bound, as it will be invoked with no args) that will
     * be called right before segueing from the table when clicking on any of the datum cells
     */
    optionalPreSegueFunction?: any
    storeResourceTableConfiguration?: any
    noResultsMessage?: string
    /** disable the datum filter for a specified resource table */
    disableDatumFilter?: boolean
    /**
     * name of querymethod on datum's model to sort by
     * if this is not defined, then datum column will not have sorting indicator
     */
    datumColumnSortingQuerymethod?: string
    /**
     * pass if you want to override the title of the datum column instead of using
     * the given dataType's label - note that datum column itself is selected by
     * the display_key attribute of the QuorumDataType
     */
    datumHeaderCellTitle?: string
    /** pass in a search trigger to show for the datum column filter */
    resourceTableDatumSearchTrigger?: any
    key?: any
    createButtonText?: string
    createButtonOnClick?: (evt?: any) => any
}

/**
This component provides an API to render tables in Quorum
See the propTypes for the required inputs
*/
export class ResourceTableContainer extends React.Component<any, any> {
    // provide sensible defaults for the table
    static defaultProps = {
        resourceTableDatumTooltipText: "",
        resourceTableTooltipText: "",
        scrollInterval: sheetConstants.TABLE_SCROLL_INTERVAL,
        shouldShowLoadingSpinnerInitially: true,
        resourceTableShowLoadingSpinner: false, // override this if you would like loading spinner control
    }

    // cannot change this to componentDidMount due to current architecture
    UNSAFE_componentWillMount() {
        this.props.initSheet(this.props.id, {
            sheet: {
                can_fit_in_memory: false,
                is_resource_table: true,
                data_type: this.props.dataType,
            },
        })

        // set the components props in the store for other components down the tree to access
        this.props.storeResourceTableConfiguration(this.props)
    }

    componentDidUpdate(prevProps) {
        // use this lifecycle hook to make sure we are updating the configuration under
        // certain conditions

        const conditions = [
            // the row filter changed
            JSON.stringify(prevProps.rowFilter) !== JSON.stringify(this.props.rowFilter),
            // if the sort key changed
            prevProps.sortKey !== this.props.sortKey,
            !areSheetColumnsEqual(prevProps.columns, this.props.columns),
        ]
        // @ts-expect-error
        if (conditions.any()) {
            this.props.storeResourceTableConfiguration(this.props)
        }
    }

    render() {
        return (
            <Col md={12} id="sheet-column">
                <div id="resource-table-wrapper">
                    <div className="sheet-wrapper-inner" id={this.props.id}>
                        <ResourceTable
                            {...this.props}
                            key={
                                // Manually propagate key since it can't be automatically propagated
                                // -- it's not a normal prop (it isn't included in this.props), but ResourceTable requires it
                                // and also requires that it stay in sync with id. For more info, see PropTypes above.
                                this.props.id
                            }
                        />
                    </div>
                </div>
            </Col>
        )
    }
}

export default connect(null, { initSheet, storeResourceTableConfiguration })(
    ResourceTableContainer,
) as React.ComponentType<ResourceTableContainerProps>
